import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import {
  DESIGNATION_OPTIONS,
  type FormSubmission,
  type GenerateFor,
  type PractitionerDesignation,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import {
  FormikCell,
  FormikCheckbox,
  FormikSelect,
  FormikTextarea,
} from "./formik-controls";

// --- Form SC2848 editable payload -------------------------------------------
// snake_case keys mirror the backend mapper's `build_defaults`
// (apps/forms/mappings/form_sc2848.py) exactly — the submission's `data` blob is
// passed through verbatim (no snake→camel mapping). Taxpayer/rep identity
// (SSN/EIN/phone/address/contact) is filled server-side from the Client /
// Practitioner records, never carried here.

export interface FormSc2848Representative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
  /** SC per-rep change flags — "the rep's ... has changed since the CAF". */
  sc_new_address: boolean;
  sc_new_phone: boolean;
  sc_new_fax: boolean;
  sc_new_email: boolean;
  /** SC jurisdiction / enrollment (bar state or card no.) printed on the declaration. */
  sc_jurisdiction: string;
}

export interface FormSc2848Matter {
  tax_type: string;
  tax_form: string;
  tax_years: string;
}

export interface FormSc2848Data {
  generate_for: GenerateFor;
  representatives: FormSc2848Representative[];
  matters: FormSc2848Matter[];
  /** Which single rep (by practitioner id) receives refund checks. */
  rec_refund_rep: string;
  /** Additions/deletions to the authorized acts (free text, ≤200 chars). */
  acts_authorized: string;
  dont_revoke: boolean;
  /** Business signature block (individual clients ignore these). */
  plan_no: string;
  title: string;
}

/** SC2848 fits three representatives and three tax-matter rows. */
const MAX_SC_REPS = 3;
const MAX_SC_MATTERS = 3;
const ACTS_MAX = 200;

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EMPTY_MATTER: FormSc2848Matter = { tax_type: "", tax_form: "", tax_years: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  acts_authorized: Yup.string().max(ACTS_MAX, "Too long"),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(60, "Too long"),
        tax_form: Yup.string().max(40, "Too long"),
        tax_years: yearRange,
      }),
    )
    .max(MAX_SC_MATTERS),
});

function toInitialValues(data: Record<string, unknown>): FormSc2848Data {
  const d = data as Partial<FormSc2848Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters:
      d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    rec_refund_rep: d.rec_refund_rep ?? "",
    acts_authorized: d.acts_authorized ?? "",
    dont_revoke: d.dont_revoke ?? false,
    plan_no: d.plan_no ?? "",
    title: d.title ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * "Power of Attorney Given To" — the client's assigned representatives. The user
 * toggles which are active, edits each one's designation, jurisdiction, and the
 * four SC change flags (address/phone/fax/email changed since the CAF). Contact
 * details are filled from the practitioner record server-side.
 */
function ScRepresentativesTable() {
  const { values } = useFormikContext<FormSc2848Data>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-44">Designation</TableHead>
            <TableHead className="w-40">Jurisdiction</TableHead>
            <TableHead className="w-44">Changed since CAF</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.sc_jurisdiction`}
                  aria-label={`Jurisdiction for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <FormikCheckbox
                    name={`representatives.${i}.sc_new_address`}
                    label="Address"
                  />
                  <FormikCheckbox
                    name={`representatives.${i}.sc_new_phone`}
                    label="Phone"
                  />
                  <FormikCheckbox
                    name={`representatives.${i}.sc_new_fax`}
                    label="Fax"
                  />
                  <FormikCheckbox
                    name={`representatives.${i}.sc_new_email`}
                    label="Email"
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** SC2848 tax-matters rows — Tax type / Tax form / Year(s), capped at three. */
function ScMattersTable() {
  const { values } = useFormikContext<FormSc2848Data>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type of Tax</TableHead>
                  <TableHead className="w-36">Tax Form Number</TableHead>
                  <TableHead className="w-44">Year(s) or Period(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Income"
                        aria-label={`Type of tax for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_form`}
                        placeholder="e.g. SC1040"
                        aria-label={`Tax form for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_SC_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_SC_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form SC2848 fits up to {MAX_SC_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

/** "Which representative receives refund checks" selector (a rep, or none). */
function RefundRepField() {
  const { values } = useFormikContext<FormSc2848Data>();
  const active = values.representatives.filter((r) => r.active);
  const options = active.map((r) => ({ value: r.practitioner, label: r.name }));
  return (
    <div className="grid gap-2">
      <Label htmlFor="rec_refund_rep">Representative to receive refund checks</Label>
      <FormikSelect
        name="rec_refund_rep"
        options={options}
        placeholder="None"
        className="max-w-sm"
        aria-label="Representative to receive refund checks"
      />
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form SC2848 (South Carolina Power of Attorney) — prefilled from the
 * client + its practitioners. Up to three reps (each with SC change flags +
 * jurisdiction) and three tax matters; overflow spills onto a continuation sheet
 * server-side. Adds the SC additions/deletions text, a do-not-revoke toggle, and
 * a refund-check routing selector. Identity is filled server-side (never typed).
 */
export function FormSc2848Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");
  const isBusiness = client.type === "Business";

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
            {isBusiness && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="title">Title</Label>
                  <FormikCell name="title" aria-label="Title" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="plan_no">Plan number</Label>
                  <FormikCell name="plan_no" aria-label="Plan number" />
                </div>
              </div>
            )}
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <ScRepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <ScMattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Acts Authorized</SectionHeading>
          <div className="grid gap-2">
            <Label htmlFor="acts_authorized">
              Additions or deletions to the authorized acts
            </Label>
            <FormikTextarea id="acts_authorized" name="acts_authorized" rows={3} />
          </div>
          <RefundRepField />
          <FormikCheckbox
            name="dont_revoke"
            label="Do not revoke prior Powers of Attorney"
          />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
